import React, { useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/TestAuthContext';
import { uploadFiles } from '../utils/api';
import { 
  Upload, 
  FileText, 
  FileSpreadsheet, 
  File, 
  X, 
  CheckCircle2, 
  AlertCircle,
  Loader2,
  ArrowRight,
  Sparkles,
  Zap,
  Clock,
  DollarSign,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import TransactionReview from '@/components/TransactionReview';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// File type icons
const FILE_ICONS: Record<string, React.ReactNode> = {
  'application/pdf': <FileText className="w-6 h-6 text-red-500" />,
  'text/csv': <FileSpreadsheet className="w-6 h-6 text-green-500" />,
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': <FileSpreadsheet className="w-6 h-6 text-emerald-500" />,
  'application/vnd.ms-excel': <FileSpreadsheet className="w-6 h-6 text-emerald-500" />,
};

const BUSINESS_TYPES = [
  'Restaurant',
  'Retail',
  'Manufacturing',
  'Service',
  'Healthcare',
  'Construction',
  'Professional Services',
  'Other',
];

const INTERRUPTION_TYPES = [
  'Hurricane',
  'Fire',
  'Flood',
  'Tornado',
  'Earthquake',
  'Civil Authority',
  'Pandemic',
  'Supply Chain',
  'Other',
];

interface UploadedFile {
  file: File;
  status: 'pending' | 'uploading' | 'success' | 'error';
  error?: string;
}

interface UploadStats {
  total_files: number;
  files_processed: number;
  files_failed: number;
  total_transactions: number;
  total_periods: number;
  cache_hits: number;
  cache_misses: number;
  total_cost_usd: number;
  total_processing_time_seconds: number;
  items_needing_review: number;
}

interface FileResult {
  filename: string;
  file_type: string;
  success: boolean;
  transactions_extracted: number;
  periods_count: number;
  extraction_method: string;
  cache_hit: boolean;
  cost_usd: number;
  processing_time_seconds: number;
  errors: string[];
  needs_review_count: number;
}

interface UploadResponse {
  success: boolean;
  message: string;
  canonical_data: any;
  file_results: FileResult[];
  review_items: any[];
  stats: UploadStats;
}

export default function UnifiedUploadPage() {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { getToken } = useAuth();
  
  // Upload state
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  
  // Metadata state
  const [companyName, setCompanyName] = useState('');
  const [businessType, setBusinessType] = useState('');
  const [interruptionType, setInterruptionType] = useState('');
  const [interruptionStart, setInterruptionStart] = useState('');
  const [interruptionEnd, setInterruptionEnd] = useState('');
  
  // Results state
  const [uploadResponse, setUploadResponse] = useState<UploadResponse | null>(null);
  const [showReview, setShowReview] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Handle file drop
  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    const droppedFiles = Array.from(e.dataTransfer.files);
    addFiles(droppedFiles);
  }, []);
  
  // Handle file select
  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const selectedFiles = Array.from(e.target.files);
      addFiles(selectedFiles);
    }
  }, []);
  
  // Add files to list
  const addFiles = (newFiles: File[]) => {
    const validFiles = newFiles.filter(file => {
      const ext = file.name.split('.').pop()?.toLowerCase();
      return ['pdf', 'csv', 'xlsx', 'xls'].includes(ext || '');
    });
    
    const uploadedFiles: UploadedFile[] = validFiles.map(file => ({
      file,
      status: 'pending',
    }));
    
    setFiles(prev => [...prev, ...uploadedFiles]);
    setError(null);
  };
  
  // Remove file
  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };
  
  // Upload files
  const uploadFiles = async () => {
    if (files.length === 0) {
      setError('Please add at least one file');
      return;
    }
    
    setIsUploading(true);
    setError(null);
    setUploadProgress(10);
    
    try {
      const formData = new FormData();
      
      // Add files
      files.forEach(({ file }) => {
        formData.append('files', file);
      });
      
      // Add metadata
      const metadata = {
        company_name: companyName,
        business_type: businessType.toLowerCase(),
        interruption_type: interruptionType || null,
        interruption_start: interruptionStart || null,
        interruption_end: interruptionEnd || null,
        enable_bert: false,
        use_cache: true,
        enable_textract: true,
        enable_claude: true,
      };
      formData.append('metadata', JSON.stringify(metadata));
      
      setUploadProgress(30);
      
      // Use centralized API utility for file upload
      const result: UploadResponse = await uploadFiles('/api/v1/upload/claim-documents', formData, { showErrorToast: true });
      
      setUploadProgress(100);
      setUploadResponse(result);
      
      // Auto-advance to review if there are items to review
      if (result.success) {
        setShowReview(true);
      }
      
    } catch (err: any) {
      setError(err.message || 'Upload failed');
    } finally {
      setIsUploading(false);
    }
  };
  
  // Handle review confirm
  const handleConfirm = async (updatedData: any) => {
    // TODO: Save updated canonical data and run calculation
    console.log('Confirmed data:', updatedData);
    
    // For now, navigate to a success page or dashboard
    // In production, this would save the data and trigger calculation
    // Data confirmed - proceed to dashboard
    navigate('/dashboard');
  };
  
  // Get file icon
  const getFileIcon = (file: File) => {
    return FILE_ICONS[file.type] || <File className="w-6 h-6 text-gray-500" />;
  };
  
  // Format file size
  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };
  
  // If showing review screen
  if (showReview && uploadResponse?.canonical_data) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <TransactionReview
          canonicalData={uploadResponse.canonical_data}
          onConfirm={handleConfirm}
          onCancel={() => setShowReview(false)}
        />
      </div>
    );
  }
  
  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Upload Claim Documents
        </h1>
        <p className="text-gray-600">
          Drop all your files at once. We'll figure out the rest.
        </p>
      </div>
      
      {/* Drop Zone */}
      <Card 
        className={`border-2 border-dashed transition-colors cursor-pointer
          ${isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400'}
          ${files.length > 0 ? 'border-solid' : ''}
        `}
        onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <CardContent className="pt-6">
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".pdf,.csv,.xlsx,.xls"
            onChange={handleFileSelect}
            className="hidden"
          />
          
          {files.length === 0 ? (
            <div className="text-center py-12">
              <Upload className="w-16 h-16 mx-auto text-gray-400 mb-4" />
              <p className="text-lg font-medium text-gray-700 mb-2">
                Drag & drop your files here
              </p>
              <p className="text-sm text-gray-500 mb-4">
                or click to browse
              </p>
              <div className="flex justify-center gap-2">
                <Badge variant="outline">PDF</Badge>
                <Badge variant="outline">CSV</Badge>
                <Badge variant="outline">Excel</Badge>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm font-medium text-gray-700">
                  {files.length} file(s) selected
                </span>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={(e) => { e.stopPropagation(); setFiles([]); }}
                >
                  Clear All
                </Button>
              </div>
              
              {files.map((uploadedFile, index) => (
                <div 
                  key={index}
                  className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg"
                  onClick={(e) => e.stopPropagation()}
                >
                  {getFileIcon(uploadedFile.file)}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {uploadedFile.file.name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {formatSize(uploadedFile.file.size)}
                    </p>
                  </div>
                  {uploadedFile.status === 'success' && (
                    <CheckCircle2 className="w-5 h-5 text-green-500" />
                  )}
                  {uploadedFile.status === 'error' && (
                    <AlertCircle className="w-5 h-5 text-red-500" />
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeFile(index)}
                    className="h-8 w-8 p-0"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ))}
              
              <Button 
                variant="outline" 
                className="w-full mt-2"
                onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
              >
                <Upload className="w-4 h-4 mr-2" />
                Add More Files
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Metadata Form */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Claim Details (Optional)</CardTitle>
          <CardDescription>
            Add context for better categorization
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">
                Company Name
              </label>
              <Input
                placeholder="e.g., Pacific Surf Restaurant"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">
                Business Type
              </label>
              <Select value={businessType} onValueChange={setBusinessType}>
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {BUSINESS_TYPES.map(type => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">
                Interruption Type
              </label>
              <Select value={interruptionType} onValueChange={setInterruptionType}>
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {INTERRUPTION_TYPES.map(type => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">
                Start Date
              </label>
              <Input
                type="date"
                value={interruptionStart}
                onChange={(e) => setInterruptionStart(e.target.value)}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">
                End Date
              </label>
              <Input
                type="date"
                value={interruptionEnd}
                onChange={(e) => setInterruptionEnd(e.target.value)}
              />
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-500" />
          <span className="text-red-700">{error}</span>
        </div>
      )}
      
      {/* Upload Results */}
      {uploadResponse && !showReview && (
        <Card className="border-green-200 bg-green-50">
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <CheckCircle2 className="w-8 h-8 text-green-600" />
              <div className="flex-1">
                <h3 className="font-semibold text-green-900 mb-2">
                  Upload Complete!
                </h3>
                <p className="text-green-700 mb-4">{uploadResponse.message}</p>
                
                {/* Stats Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                  <div className="bg-white rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-gray-900">
                      {uploadResponse.stats.total_transactions}
                    </div>
                    <div className="text-xs text-gray-500">Transactions</div>
                  </div>
                  <div className="bg-white rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-gray-900">
                      {uploadResponse.stats.total_periods}
                    </div>
                    <div className="text-xs text-gray-500">Periods</div>
                  </div>
                  <div className="bg-white rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-gray-900">
                      {uploadResponse.stats.cache_hits}
                    </div>
                    <div className="text-xs text-gray-500">Cache Hits</div>
                  </div>
                  <div className="bg-white rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-gray-900">
                      {uploadResponse.stats.total_processing_time_seconds.toFixed(2)}s
                    </div>
                    <div className="text-xs text-gray-500">Time</div>
                  </div>
                </div>
                
                {/* File Results */}
                <div className="space-y-2">
                  {uploadResponse.file_results.map((fr, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm">
                      {fr.success ? (
                        <CheckCircle2 className="w-4 h-4 text-green-600" />
                      ) : (
                        <AlertCircle className="w-4 h-4 text-red-500" />
                      )}
                      <span className="font-medium">{fr.filename}</span>
                      <span className="text-gray-500">
                        {fr.transactions_extracted} txns
                      </span>
                      {fr.cache_hit && (
                        <Badge variant="outline" className="text-xs">
                          <Zap className="w-3 h-3 mr-1" />
                          Cached
                        </Badge>
                      )}
                    </div>
                  ))}
                </div>
                
                {/* Review Button */}
                {uploadResponse.stats.items_needing_review > 0 && (
                  <Button 
                    className="mt-4 bg-amber-600 hover:bg-amber-700"
                    onClick={() => setShowReview(true)}
                  >
                    <AlertCircle className="w-4 h-4 mr-2" />
                    Review {uploadResponse.stats.items_needing_review} Items
                  </Button>
                )}
                
                {uploadResponse.stats.items_needing_review === 0 && (
                  <Button 
                    className="mt-4 bg-green-600 hover:bg-green-700"
                    onClick={() => setShowReview(true)}
                  >
                    <ArrowRight className="w-4 h-4 mr-2" />
                    Continue to Review
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Upload Button */}
      {!uploadResponse && (
        <Button
          onClick={uploadFiles}
          disabled={files.length === 0 || isUploading}
          className="w-full h-14 text-lg bg-blue-600 hover:bg-blue-700"
        >
          {isUploading ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Processing... {uploadProgress}%
            </>
          ) : (
            <>
              <Sparkles className="w-5 h-5 mr-2" />
              Upload & Analyze
              <ArrowRight className="w-5 h-5 ml-2" />
            </>
          )}
        </Button>
      )}
      
      {/* Features Banner */}
      <div className="grid grid-cols-3 gap-4 text-center text-sm text-gray-600">
        <div className="flex flex-col items-center gap-2">
          <Zap className="w-6 h-6 text-amber-500" />
          <span>Smart Caching</span>
        </div>
        <div className="flex flex-col items-center gap-2">
          <Clock className="w-6 h-6 text-blue-500" />
          <span>Parallel Processing</span>
        </div>
        <div className="flex flex-col items-center gap-2">
          <DollarSign className="w-6 h-6 text-green-500" />
          <span>Cost Optimized</span>
        </div>
      </div>
    </div>
  );
}
